import { RankKit } from "@/components/site/RankKit";
import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { TableOfContents, Section, SectionHeader, CTABand, FeatureGrid, FAQ, StatRow, Breadcrumbs, InlineCTA } from "@/components/site/Blocks";
import { TLDR, DscrCalculator, ProsCons, ExpertInsight } from "@/components/site/SeoBlocks";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/how-dscr-works";
const HERO = getDscrClusterHero(URL);

const KEYWORDS = [
  "how DSCR loans work",
  "DSCR ratio explained",
  "rent to PITIA ratio",
  "DSCR loan formula",
  "debt service coverage ratio calculation",
  "DSCR loan qualification",
  "how to calculate DSCR",
  "DSCR loan LTV",
  "DSCR loan reserves requirement",
  "DSCR loan credit score",
  "no income verification loan explained",
  "DSCR loan process step by step",
  "DSCR loan closing timeline",
  "DSCR vs conventional investment loan",
].join(", ");

const FAQS = [
  {
    q: "What does DSCR actually measure?",
    a: "It measures whether the property pays for itself. Gross monthly rent is divided by PITIA — principal, interest, taxes, insurance, and association dues. A 1.00 DSCR means rent exactly covers the payment; 1.25 means rent covers it with 25% to spare.",
  },
  {
    q: "Which rent number does the lender use?",
    a: "The lower of the in-place lease and the appraiser's market rent on Form 1007 (single unit) or Form 1025 (2–4 units). A vacant property is underwritten on market rent alone.",
  },
  {
    q: "Does my personal income matter at all?",
    a: "No. There is no debt-to-income calculation, no tax returns, no W-2s, and no pay stubs. Credit, reserves, and the property's own numbers carry the file.",
  },
  {
    q: "What happens if the ratio comes in below 1.00?",
    a: "Options remain. You can increase the down payment, buy the rate down, or move to a No-Ratio structure that replaces the ratio test with a larger down payment and a rate premium.",
  },
  {
    q: "How long does a DSCR loan take to close?",
    a: "Typically 21–30 days from a complete file. The appraisal with rent schedule and the insurance binder are usually the two items that set the pace.",
  },
  {
    q: "Can I use a DSCR loan for a short-term rental?",
    a: "Yes on select programs, underwritten on documented platform revenue or a market projection instead of a long-term lease. Leverage is generally lower than on a standard long-term rental.",
  },
  {
    q: "What is considered a 'good' DSCR for approval?",
    a: "1.00 or higher qualifies for standard leverage on most programs, and 1.25+ typically reaches the best pricing tier. Ratios below 1.00 can still work through an expanded matrix or a No-Ratio structure, generally with a lower maximum LTV.",
  },
  {
    q: "Does DSCR include property management fees?",
    a: "No. The DSCR formula divides gross rent by PITIA only — principal, interest, taxes, insurance, and HOA dues. Management fees, maintenance, and other operating expenses are not part of the underwriting ratio, even though you should budget for them.",
  },
  {
    q: "Can I count multiple units' rent toward DSCR on a 2–4 unit property?",
    a: "Yes. The appraiser's Form 1025 reports market rent for each unit, and the total gross rent for the property is used against the single blended PITIA payment to calculate the ratio.",
  },
  {
    q: "Is DSCR calculated differently for a purchase versus a refinance?",
    a: "The formula is the same, but the rent input differs: a purchase generally uses the appraiser's market rent (or an executed lease if in place), while a refinance can use the current in-place lease if it's at or above market, subject to the appraisal.",
  },
  {
    q: "Does a co-borrower's rent history help my DSCR?",
    a: "DSCR is calculated on the property, not the borrower, so a co-borrower's personal rental history isn't a qualifying factor. What matters is the subject property's documented or projected rent relative to its own payment.",
  },
  {
    q: "How is DSCR different from debt-to-income (DTI)?",
    a: "DTI compares your personal income and debts; DSCR compares only the property's rent to its own payment and is calculated with no reference to your personal earnings, tax returns, or existing debt load.",
  },
];

export const Route = createFileRoute("/how-dscr-works")({
  head: () =>
    buildHead({
      title: "How DSCR Loans Work: The Ratio, The Math, The Process",
      description:
        "A plain-English walkthrough of DSCR loans — how rent ÷ PITIA is calculated, which rent figure lenders use, what moves your leverage, and every step from scenario to closing.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "How DSCR Works", url: URL },
      ],
      service: { name: "DSCR Loan Education & Qualification" },
      faqs: FAQS,
    }),
  component: HowDSCR,
});

const STEPS: { title: string; body: string }[] = [
  { title: "1. Scenario review", body: "You send the address, expected rent, purchase price or value, and a credit range. We size the ratio and the tier before anything is ordered." },
  { title: "2. Term sheet", body: "Rate, leverage, reserve requirement, and estimated closing costs in writing so you can compare the deal against your own return targets." },
  { title: "3. Appraisal & rent schedule", body: "A full interior appraisal plus Form 1007 or 1025. This is the step that confirms — or reprices — the ratio the term sheet assumed." },
  { title: "4. Underwriting", body: "Entity documents, ID, insurance binder, and bank statements for reserves. No tax returns and no income documentation at any point." },
  { title: "5. Closing", body: "Typically 21–30 days from a complete file, usually vested in your LLC at no pricing premium." },
];

function HowDSCR() {
  return (
    <SiteShell hideBreadcrumbs productLabel="How DSCR Works">
      <PageHero
        eyebrow="DSCR · Education"
        title="How DSCR loans work"
        subtitle="A DSCR loan asks one question: does the rent cover the payment? Divide gross rent by PITIA, clear the program minimum, and the file works — regardless of what your tax returns say."
        image={HERO}
        primaryHref="/apply?product=dscr"
      />
      <RankKit page="how-dscr-works" part="answer" />
      <PageEssentials hideAnswer hideTldr hideToc />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR Loans", href: "/dscr-loans" }, { name: "How DSCR Works" }]} />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#formula", label: "The formula" },
          { href: "#calculator", label: "Calculator" },
          { href: "#variables", label: "Key variables" },
          { href: "#process", label: "The process" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <Section id="tldr">
        <TLDR
          points={[
            "DSCR = gross monthly rent ÷ PITIA (principal, interest, taxes, insurance, association dues).",
            "Lenders use the lower of the in-place lease and appraised market rent (Form 1007 / 1025).",
            "No tax returns, no W-2s, no pay stubs, and no debt-to-income calculation.",
            "Credit, leverage, and the ratio together decide pricing — 720+ FICO and 1.20+ DSCR reach the best tier.",
            "Business-purpose only: the property must be non-owner-occupied and held as an investment.",
          ]}
        />
        <div className="mt-8">
          <StatRow
            items={[
              { label: "Min FICO", value: "660" },
              { label: "Min DSCR", value: "0.80 floor" },
              { label: "Max LTV", value: "80%" },
              { label: "Typical close", value: "21–30 days" },
            ]}
          />
        </div>
      </Section>

      <Section id="formula">
        <SectionHeader
          eyebrow="The formula"
          title="Rent ÷ PITIA = DSCR"
          intro="PITIA is the full housing payment, not just principal and interest. Leaving taxes, insurance, or HOA dues out is the single most common reason an investor's own math disagrees with the lender's."
        />
        <div className="mt-10 rounded-xl border border-border bg-card p-8 text-center">
          <div className="font-display text-3xl font-bold text-brand sm:text-4xl">$2,400 rent ÷ $2,000 PITIA = 1.20 DSCR</div>
          <p className="mt-3 text-muted-foreground">1.20+ reaches best pricing on most programs. Down to a 0.80 floor with adjustments, and No-Ratio structures below that.</p>
        </div>
        <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Ratio</th>
                <th className="px-4 py-3">What it means</th>
                <th className="px-4 py-3">Practical effect</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                ["1.25+", "Rent covers PITIA with 25% cushion", "Best pricing tier and full leverage"],
                ["1.00–1.24", "Rent covers the payment", "Standard pricing, up to 80% LTV"],
                ["0.80–0.99", "Property runs slightly short", "Available with a rate adjustment and lower LTV"],
                ["Below 0.80", "Property does not carry itself", "No-Ratio structure: larger down payment, rate premium"],
              ].map((row) => (
                <tr key={row[0]}>
                  <td className="px-4 py-3 font-semibold text-foreground">{row[0]}</td>
                  <td className="px-4 py-3 text-foreground">{row[1]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Illustrative only. Eligibility, terms, and pricing vary by lender, property, and state, and are subject to underwriting and approval. Not a commitment to lend.
        </p>
      </Section>

      <LiveDscrRateSection
            strategyCards
        id="live-rates"
        eyebrow={"Live pricing · The mechanics"}
        heading={"How the DSCR Calculation Connects to Your Rate"}
        paragraphs={[
            "Once the coverage calculation makes sense, pricing is easier to follow: the ratio is one of the inputs each investor prices on, alongside leverage, credit profile and property type, and the engine returns the rate, APR and points or credit you see below for that combination.",
            "Every row below comes from one live pricing response, so you can trace a single quote end to end — the rate, the APR shown for that quote, the cash cost or credit, and the resulting monthly payment.",
        ]}
        faqs={[
            { q: "Does a higher DSCR always get a lower rate?", a: "Coverage ratio is one of the inputs investors price on, and most programs stop improving pricing above a certain level of coverage. Where that point sits, and how much it is worth, is program-specific." },
            { q: "Is the rate set before or after the appraisal?", a: "Indicative pricing comes first; final pricing depends on the appraisal, the appraiser's market rent, credit review and lender underwriting approval." },
        ]}
        links={[
            { to: "/dscr-calculator", label: "Run the DSCR calculator" },
            { to: "/investment-property-rates", label: "Price your scenario live" },
            { to: "/dscr-loan-rates", label: "Review pricing tiers" },
        ]}
      />

      <Section id="calculator">
        <SectionHeader eyebrow="Run your own numbers" title="DSCR calculator" intro="Enter rent and the payment components to see the ratio a lender would compute on your deal." />
        <div className="mt-8">
          <DscrCalculator productLabel="DSCR" />
        </div>
      </Section>

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>

      <div className="bg-muted/40">
        <Section id="variables">
          <SectionHeader eyebrow="What matters" title="The variables that move your deal" />
          <div className="mt-10">
            <FeatureGrid
              items={[
                { title: "Credit score", body: "660 minimum on most programs; 700+ improves leverage; 720+ reaches the best pricing tier." },
                { title: "LTV / down payment", body: "Up to 80% LTV on purchase and limited cash-out; 75% on cash-out. 20–25% down is typical." },
                { title: "DSCR ratio", body: "1.20+ for best pricing, 1.00 for full leverage, 0.80 floor with adjustments." },
                { title: "Reserves", body: "1–6 months of PITIA verified at closing, scaled by loan size, leverage, and ratio." },
                { title: "Rent documentation", body: "Executed lease or the appraiser's Form 1007 / 1025 market rent — the lender uses the lower figure." },
                { title: "Property type", body: "1–4 unit non-owner-occupied residential, plus 5–8 unit on select programs. Condotels and rural land are restricted." },
              ]}
            />
          </div>
        </Section>
      </div>

      <Section id="process">
        <SectionHeader eyebrow="Step by step" title="What the process actually looks like" />
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {STEPS.map((s) => (
            <div key={s.title} className="rounded-xl border border-border bg-card p-5">
              <div className="font-display text-base font-semibold text-foreground">{s.title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <ProsCons
        title="Where a DSCR loan fits — and where it does not"
        pros={[
          "No income documentation and no debt-to-income ceiling",
          "Unlimited financed properties, unlike agency limits",
          "LLC vesting standard at no pricing premium",
          "Self-employed, 1099, and foreign national investors are not penalized",
        ]}
        cons={[
          "The property must carry itself or you add down payment",
          "Reserves must be verified and seasoned",
          "Business purpose only — never a primary or second home",
          "A weak appraisal rent schedule can reprice the deal late",
        ]}
      />

      <InlineCTA
        variant="soft"
        title="Want the ratio run on a real address?"
        body="Send the address, expected rent, and a credit range — we'll tell you the tier your deal lands in before anything is ordered."
        href="/apply?product=dscr"
      />


      <DscrProgramLinks currentPath={URL} />

      <RankKit page="how-dscr-works" part="details" />
      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="How DSCR loans work — common questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          Guidelines shown are illustrative and vary by lender, property, and state. Eligibility, terms, and availability
          are subject to underwriting and approval. Not a commitment to lend. Business-purpose loans only.
        </p>
      </Section>

      <DscrCrossProductLinks currentPath={URL} />

      <ExpertInsight insightKey="how-dscr-works" />

      <CTABand
        title="See which DSCR tier your property lands in"
        body="A short pre-qualification request is all it takes to get a scenario review."
        href="/apply?product=dscr"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
